import { alignNotes } from './align-notes.js'
import {
  closeBandCents,
  fullScoreCents,
  octaveToleranceCents,
  onBandCents,
  stabilityIqrCents,
  zeroScoreCents,
} from './constants.js'
import type { SungNote } from './types.js'

export type PitchBand = 'on' | 'close' | 'off' | 'wrong-note'

export interface NoteScore {
  /** Signed cents from the reference pitch. Negative is flat. */
  cents: number
  band: PitchBand
  /** 1 at or under 10 cents, falling linearly to 0 at 50. */
  score: number
}

export interface IntervalScore {
  /** Expected interval between consecutive reference notes, in semitones. */
  expected: number
  /** The interval actually sung, in fractional semitones. */
  sung: number
  /** Signed cents error of the sung interval. */
  cents: number
}

export type Stability = 'steady' | 'wavering'

export interface PhraseScore {
  absolute: {
    perNote: NoteScore[]
    /** Mean of the per-note scores. */
    mean: number
  }
  relative: {
    perInterval: IntervalScore[]
    /**
     * The phrase's mean signed cents offset from the reference: negative is flat overall.
     *
     * Read together with a per-interval error near zero this is §5.5's headline finding — the
     * singer's relative pitch is intact and the whole phrase has simply settled below the
     * reference. That is a support or anchor problem, not an ear problem, and it points at a
     * completely different practice remedy from "you sing out of tune".
     *
     * Wrong notes are excluded from the mean, because a wrong note is a melodic error and not a
     * tuning offset: one note sung an octave out reported +240 c of drift on a phrase whose
     * every other note was exactly on pitch. `undefined` when no note was close enough to
     * measure an offset from at all — which is not the same as a drift of zero, and a caller
     * has to say something different about it.
     */
    driftCents: number | undefined
  }
  /** Per paired note, index-aligned with `absolute.perNote`. Reported, never scored (§5.6). */
  stability: Stability[]
  /** -1, 0 or +1 octaves: the whole phrase was sung displaced by this much (§5.7). */
  octave: -1 | 0 | 1
  /** Indices into `reference` the singer did not sing. */
  missed: number[]
  /** Indices into `sung` that are not in the reference. */
  extra: number[]
}

const bandOf = (sungMidi: number, referenceMidi: number): PitchBand => {
  /**
   * The nearest semitone, not the nearest pitch class. §5.4's table says "pitch class", but
   * §5.7 requires a single octave-flipped note to read as a wrong note and its pitch class is
   * unchanged. Nearest-semitone subsumes the pitch-class rule and satisfies both.
   */
  if (Math.round(sungMidi) !== Math.round(referenceMidi)) {
    return 'wrong-note'
  }
  const deviation = Math.abs(sungMidi - referenceMidi) * 100
  if (deviation <= onBandCents) {
    return 'on'
  }
  return deviation <= closeBandCents ? 'close' : 'off'
}

const scoreOf = (cents: number) => {
  const deviation = Math.abs(cents)
  if (deviation <= fullScoreCents) {
    return 1
  }
  if (deviation >= zeroScoreCents) {
    return 0
  }
  return (zeroScoreCents - deviation) / (zeroScoreCents - fullScoreCents)
}

/**
 * The whole-phrase octave rule (§5.7): a phrase is octave-displaced only when *every* paired
 * note is offset by +/-12 semitones within tolerance. A bass singing a soprano phrase down an
 * octave has sung it correctly.
 */
const phraseOctave = (deltas: number[]): -1 | 0 | 1 => {
  if (deltas.length === 0) {
    return 0
  }
  const tolerance = octaveToleranceCents / 100
  if (deltas.every(delta => Math.abs(delta - 12) <= tolerance)) {
    return 1
  }
  return deltas.every(delta => Math.abs(delta + 12) <= tolerance) ? -1 : 0
}

export const scorePhrase = (sung: SungNote[], reference: number[]): PhraseScore | null => {
  const { pairs, missed, extra } = alignNotes(sung, reference)
  if (pairs.length === 0) {
    /**
     * Nothing was heard, or nothing that could be matched. A caller must be able to tell this
     * apart from a score of zero — "we didn't hear anything" is a first-class outcome and not
     * an error path — so it is `null`, never a zeroed PhraseScore.
     * @see docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §9
     */
    return null
  }

  const deltas = pairs.map(pair => sung[pair.sungIndex].midi - reference[pair.referenceIndex])
  const octave = phraseOctave(deltas)
  /** Score on the transposed pitches when the whole phrase is displaced. */
  const sungMidis = pairs.map(pair => sung[pair.sungIndex].midi - octave * 12)

  const perNote: NoteScore[] = pairs.map((pair, index) => {
    const referenceMidi = reference[pair.referenceIndex]
    const cents = (sungMidis[index] - referenceMidi) * 100
    return { cents, band: bandOf(sungMidis[index], referenceMidi), score: scoreOf(cents) }
  })

  const perInterval: IntervalScore[] = pairs.slice(1).map((pair, index) => {
    const expected = reference[pair.referenceIndex] - reference[pairs[index].referenceIndex]
    const sungInterval = sungMidis[index + 1] - sungMidis[index]
    return { expected, sung: sungInterval, cents: (sungInterval - expected) * 100 }
  })

  const mean = perNote.reduce((total, entry) => total + entry.score, 0) / perNote.length

  const inTune = perNote.filter(entry => entry.band !== 'wrong-note')
  const driftCents =
    inTune.length === 0 ? undefined : inTune.reduce((total, entry) => total + entry.cents, 0) / inTune.length

  return {
    absolute: { perNote, mean },
    relative: { perInterval, driftCents },
    stability: pairs.map(pair => (sung[pair.sungIndex].iqrCents <= stabilityIqrCents ? 'steady' : 'wavering')),
    octave,
    missed,
    extra,
  }
}
